import React, { useMemo,useState, useContext, useEffect } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase/config";
import { Tab, TabGroup, TabList } from "@headlessui/react";
import clsx from "clsx";
import StudentGroupsSelector from "./StudentGroupsSelector";
import PerkinsAllHeatmap from "./PerkinsSingleYearHeatmap";
import IndicatorsGlobalPanel from "./IndicatorsGlobalPanel";
import SimplifiedPerkinsHeatmap from "./SimplifiedPerkinsHeatmap";
// Context
import { ProductsContext } from "../context/products.context";
import CTEOpenAITest from "./CTEOpenAITest";
// Child components
import PerformanceIndicatorsGrid from "./PerformanceIndicatorsGrid";
import PerformanceIndicatorsBarChart from "./PerformanceIndicatorsBarChart";
// Other controls/components
import LeftSidebarIndicators from "./LeftSidebarIndicators";
import OptionsDualCredit from "./OptionsDualCredit";
import OptionsIndicatorsGrade from "./OptionsIndicatorsGrade";
import YearSelector from "./YearSelector";
import Checkbox from "./PerformanceCheckbox";
import PerkinsData from '../data/PerkinsExample.json'
import PerkinsIndicatorsView from "./PerkinsIndicatorsView";
import PerkinsDashboardView from "./PerkinsDashboardView";
import { allIndicatorsSubcategory, allIndicatorsCategory } from "../data/indicatorsData";
import PerkinsHeatmapView from "./PerkinsHeatmap/PerkinsHeatmapView";
import AccordionPerformanceDashboard from "./PerkinsPerformanceTablesGrid/AccordionPerformanceDashboard";
import { PerkinsPerformanceTablesGrid } from "./PerkinsSimpleTable";
export const PerformanceIndicators = () => {
  const {
    // Pulled from your context
    defaultDistrict,
    districtName,
    indicatorData,
    setIndicatorData,
    indicatorsAggData,
    setIndicatorsAggData,
    checkedStudentGroup,
    setCheckedStudentGroup,
    availStudentGroups,
    setAvailStudentGroups,
    setSelectableSchools,
    viewType,setViewType,

    // Category & Subcategory
    indicatorsCategory,
    setIndicatorsCategory,
    indicatorsSubcategory,
    setIndicatorsSubcategory,

    // Selected filters
    selectedFilterIndicatorSchool,
    setSelectedFilterIndicatorSchool,
    selectedFilterIndicatorGrade,
    setSelectedFilterIndicatorGrade,
    selectedFilterIndicatorDateNew,
    perkinsSelectedYear,
    selectedDistrictComparison,

    // Possibly for sorting
    stateToggle,
    setStateToggle,
  } = useContext(ProductsContext);



  const effectiveYear = indicatorsCategory.value === "Perkins V Indicators"
  ? perkinsSelectedYear
  : selectedFilterIndicatorDateNew;
  
 // Find which tab is currently selected by index:
 const selectedIndex = allIndicatorsCategory.findIndex(
  (cat) => cat.value === indicatorsCategory.value
);

function handleTabClick(cat) {
  setIndicatorsCategory(cat);

  // Reset grade filter to "All Grades" when switching to categories that don't use grade filtering
  if (cat.value !== "Dual Credit" && cat.value !== "Regular Attendance") {
    setSelectedFilterIndicatorGrade({ value: "All Grades", label: "All Grades 9-12" });
  }

  // Reset school to "District Total" when switching to Perkins (no school-level data available)
  if (cat.value === "Perkins V Indicators") {
    setSelectedFilterIndicatorSchool({ value: "District Total", label: "District Total" });
  }

  // Example: default subcategories for special categories
  if (cat.value === "Dual Credit") {
    setIndicatorsSubcategory(allIndicatorsSubcategory[9]);
  } else if (cat.value === "Perkins V Indicators") {
    setIndicatorsSubcategory({
      label: "1S1 - Graduation",
      value: "1S1: Four-Year Graduation Rate",
      category: "Perkins V Indicators",
      database: "perkinsIndicators_2025",
      aggDatabase: "perkinsIndicators_2025",
      title:
        "The percentage of CTE concentrators who graduate high school, as measured by the four-year adjusted cohort graduation rate.",
      subtitle: "CTE concentrators graduated from high school on time.",
      caption: "The percent of students",
    });
  } else {
    // For categories that do NOT have subcategories...
    setIndicatorsSubcategory(null);
  }
}
  // --------------------------------------------
  // DATA FETCHING / USE EFFECTS
  // --------------------------------------------
const dbName = 
  indicatorsCategory?.database 
  || indicatorsSubcategory?.database;

useEffect(() => {
  if (!defaultDistrict || !dbName) return;

  const fetchIndicatorData = async () => {
    const docRef  = doc(db, dbName, defaultDistrict.value);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) return;

    const fetchedData = docSnap.data().data || [];
    // ── build filtered, deduped studentGroups ───────────────────────────────
    const isPerkins     = indicatorsCategory.value === "Perkins V Indicators";

    // Normalize Perkins pct values: database now stores whole percentages (53.90)
    // but visualization expects decimals (0.5390)
    if (isPerkins) {
      fetchedData.forEach(entry => {
        if (entry.data && Array.isArray(entry.data)) {
          entry.data.forEach(school => {
            if (school.pct && Array.isArray(school.pct)) {
              school.pct = school.pct.map(v =>
                v != null && v !== -10 ? v / 100 : v
              );
            }
          });
        }
      });
    }

    setIndicatorData(fetchedData);
    const indicatorKey  = isPerkins
      ? indicatorsSubcategory.value.slice(0, 3)
      : null;

    const groupsForThisIndicator = fetchedData
      .filter((f) => {
        // always only “All Grades”
        if (f.gradeLevel !== "All Grades") return false;
        // if Perkins, also require the matching subcategory
        if (isPerkins && f.subcategory !== indicatorKey) return false;
        return true;
      })
      .map((f) => f.studentGroup);

    setAvailStudentGroups(
      Array.from(new Set(groupsForThisIndicator))
    );

    // ── build unique school list ─────────────────────────────────────────────
    const uniqueSchools = Array.from(
      new Set(
        fetchedData
          .flatMap((d) => d.data.map((e) => e.schoolName))
      )
    );
    setSelectableSchools(uniqueSchools);
  };

  fetchIndicatorData();
}, [
  defaultDistrict,  // refetch if the district changes
  dbName            // refetch if we switch to a different collection
]);

  useEffect(() => {
    // Fetch the aggregated data for “Comparison” (District/State/Etc)
    const fetchAggData = async () => {
      if (!defaultDistrict) return;

      const aggDbName =
        indicatorsCategory.aggDatabase || indicatorsSubcategory.aggDatabase;

      if (!aggDbName || !selectedDistrictComparison?.value) return;

      const docRef = doc(db, aggDbName, selectedDistrictComparison.value);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const aggData = docSnap.data().data || [];

        // Normalize Perkins pct values for comparison data
        if (indicatorsCategory.value === "Perkins V Indicators") {
          aggData.forEach(entry => {
            if (entry.pct && Array.isArray(entry.pct)) {
              entry.pct = entry.pct.map(v =>
                v != null && v !== -10 ? v / 100 : v
              );
            }
          });
        }

        setIndicatorsAggData(aggData);
      }
    };

    fetchAggData();
  }, [
    defaultDistrict,
    indicatorsCategory,
    indicatorsSubcategory,
    selectedDistrictComparison,
    setIndicatorsAggData,
  ]);

  // --------------------------------------------
  // RENDER
  // --------------------------------------------
    return (
      <div className="w-full max-w-[2000px] mx-auto bg-zinc-50">
      {/* Sticky “tabs” up top for main categories */}
      <div className="z-30 w-full h-10 mt-6">
        <div className="relative w-full h-20">
          {/* --- Background split (zinc top, white bottom) --- */}
          <div className="h-1/2 bg-zinc-200" />
          <div className="h-1/4 bg-zinc-50" />

          {/* --- Tabs container (absolutely centered over the split background) --- */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="flex w-full">
              {/* Left faux tab */}
              <div
                className={clsx(
                  "flex-1 h-10 bg-zinc-200 border-b-2",
                  selectedIndex === 0 && "rounded-br-lg"
                )}
              />
              
              {/* The Tab.Group is now fully controlled */}
              <Tab.Group
                selectedIndex={selectedIndex}
                onChange={(newIndex) => {
                  handleTabClick(allIndicatorsCategory[newIndex]);
                }}
              >
                <Tab.List className="flex space-x-0">
                  {allIndicatorsCategory.map((cat, idx) => {
                    return (
                      <Tab
                        key={cat.label}
                        disabled={cat.disabled}
                        className={({ selected }) =>
                          clsx(
                            "min-w-[6rem] h-10 px-7 py-1 text-center focus:outline-none border-4 border-transparent",
                            selected
                              ? "bg-zinc-50 text-zinc-700 rounded-lg font-semibold"
                              : "bg-zinc-200 text-zinc-600 hover:bg-zinc-300 hover:text-zinc-700 rounded-t-lg hover:border-zinc-200",
                            // Add a rounded edge if next to the selected tab
                            !selected && idx === selectedIndex - 1 && "rounded-br-lg",
                            !selected && idx === selectedIndex + 1 && "rounded-bl-lg"
                          )
                        }
                      >
                        {cat.label}
                      </Tab>
                    );
                  })}
                </Tab.List>
              </Tab.Group>

              {/* Right faux tab */}
              <div
                className={clsx(
                  "flex-1 h-10 bg-zinc-200 border-b-2",
                  selectedIndex === allIndicatorsCategory.length - 1 &&
                    "rounded-bl-lg"
                )}
              />
            </div>
          </div>
        </div>
      </div>



        <div className="flex">
          {/* Left Sidebar */}
          <div className="relative">
            {/* <LeftSidebarIndicators /> */}
          </div>

          {/* Main Section */}
          <div className="flex-1 pl-4">
            {/* Top Options row (subcategories, date, grade, comparison, etc.) */}
            
            {/* Student Group checkboxes, if needed */}
          

          {/* Top Row Section Styled as a Material 3 "Surface" */}
  <div className="p-4  bg-zinc-50 rounded-t-lg mt-6">  <h1 className="text-2xl font-bold text-zinc-800 py-2 border-b border-zinc-200 mb-4">Perkins V Indicators of Performance</h1>
    <div className="flex items-center justify-between justify-items-center gap-4">
   <div className="flex flex-row gap-4 items-end">
    <OptionsDualCredit/>
    <div className="inline-block mt-0.5">
      <span className="text-sm font-medium invisible">Filter:</span>
      <StudentGroupsSelector
        allGroups={availStudentGroups}
        checkedGroups={checkedStudentGroup}
        onChange={(group) => {
          const alreadySelected = checkedStudentGroup.includes(group);
          if (alreadySelected) {
            setCheckedStudentGroup(
              checkedStudentGroup.filter((g) => g !== group)
            );
          } else {
            setCheckedStudentGroup([...checkedStudentGroup, group]);
          }
        }}
        onSelectAll={() => setCheckedStudentGroup(availStudentGroups)}
        onDeselectAll={() => setCheckedStudentGroup([])}
        onToggleSort={() => setStateToggle((prev) => !prev)}
        sortAlphabetical={stateToggle}
      />
    </div>
    {(indicatorsCategory.value === "Dual Credit" || indicatorsCategory.value === "Regular Attendance") && (
      <OptionsIndicatorsGrade />
    )}</div> 
    
      {/* Right Group: Options & Toggle Buttons */}
      <div className="flex flex-wrap items-center gap-4">
        {/* Example Option Buttons */}

       
      </div>
    </div>
  </div> 

<PerformanceIndicatorsBarChart

                indicatorData={indicatorData}
                indicatorsSubcategory={indicatorsSubcategory}
                indicatorsCategory={indicatorsCategory}
                indicatorsAggData={indicatorsAggData}
                checkedStudentGroup={checkedStudentGroup}
                selectedFilterIndicatorSchool={selectedFilterIndicatorSchool}
                selectedDistrictComparison={selectedDistrictComparison}
                districtName={districtName}
                selectedYear={effectiveYear}
                selectedFilterIndicatorGrade={selectedFilterIndicatorGrade}
                selectedFilterIndicatorDateNew={selectedFilterIndicatorDateNew}
                stateToggle={stateToggle}
                viewType={viewType}
              />
              {/* <PerkinsDashboardView/>
 */}

          {/* <SimplifiedPerkinsHeatmap  perkinsData={PerkinsData}/> */}
          {/* <PerkinsPerformanceTablesGrid  perkinsData={PerkinsData}/> */}
          {/* <PerkinsHeatmapView checkedStudentGroup={checkedStudentGroup} perkinsData={PerkinsData}/> */}
          </div>
        </div>
      </div>
    );
  };
